import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import { CommonState, DataProtectionConfigService } from "@obg/ngx.common";
import {
	AppError,
	AuthTransactionAction,
	AuthTransactionConfigService,
	AuthTransactionSelector,
	CORE_ROUTE,
	COUNTRY_OF_REGISTRATION,
	CustomerManagementAction,
	HIDE_GROUP_CONSENT_COUNTRY_OF_REGISTRATION,
	JurisdictionSelector,
	MarketSelector,
	MessageScopeType,
	MessageService,
	MessageType,
	ProductType,
	SessionAction,
	VerificationStatus,
} from "@obg/ngx.core";
import { PlatformService, DocumentRef } from "@obg/ngx.global.evo";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { asyncScheduler, interval, merge, of } from "rxjs";
import {
	catchError,
	exhaustMap,
	filter,
	map,
	mergeMap,
	observeOn,
	switchMap,
	take,
	timeout,
	withLatestFrom,
	tap,
} from "rxjs/operators";

import { RegistrationFeatureState } from "../state";

import { AAMS_REGISTRATION_ACTION_TYPE } from "./aams/aams-registration.action";
import { DGA_REGISTRATION_ACTION_TYPE } from "./dga/dga-registration.action";
import {
	REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE,
	RegistrationWithPersonalNumberAction,
} from "./personal-number/registration-with-personal-number.action";
import { RegistrationWithPersonalNumberSelector } from "./personal-number/registration-with-personal-number.selector";
import { REGISTRATION_ACTION_TYPE, RegistrationAction } from "./registration.action";
import { RegistrationConfigService } from "./registration.config";
import { REGISTER_MESSAGE_CODE } from "./registration.const";
import {
	CPFRegistrationPayload,
	RegistrationCustomEvent,
	RegistrationPayload,
	RegistrationResponse,
	RegistrationStatusResponse,
} from "./registration.model";
import { RegistrationSelector } from "./registration.selector";
import { RegistrationService } from "./registration.service";
import { REGISTRATION_WITH_SMART_ID_ACTION_TYPE } from "./smart-id/registration-with-smart-id.action";
import { REGISTRATION_WITH_SPID_ID_ACTION_TYPE } from "./spid/registration-with-spid.action";

@Injectable()
export class RegistrationEffect {
	getRegistrationToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_ACTION_TYPE.getRegistrationToken),
			map((x: ActionWithPayload<RegistrationPayload>) => x.payload),
			withLatestFrom(
				this.store.select(JurisdictionSelector.get),
				this.store.select(this.marketSelector.getCurrent()),
				this.store.select<{ product: ProductType | undefined } | undefined>(RouteSelector.getQueryParams)
			),
			switchMap(([request, jurisdiction, market, params]) =>
				this.service.register(request, jurisdiction!, market, params?.product).pipe(
					map((response: RegistrationResponse) =>
						this.action.getRegistrationTokenSuccess(response, request.credential)
					),
					catchError((response: AppError) => of(this.action.getRegistrationTokenFail(response)))
				)
			)
		)
	);

	getCPFRegistrationToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_ACTION_TYPE.getCPFRegistrationToken),
			map((action: ActionWithPayload<CPFRegistrationPayload>) => action.payload),
			withLatestFrom(
				this.store.select(JurisdictionSelector.get),
				this.store.select(this.marketSelector.getCurrent()),
				this.store.select<{ product: ProductType | undefined } | undefined>(RouteSelector.getQueryParams)
			),
			exhaustMap(([request, jurisdiction, market, params]) =>
				this.service.registerCPFFlow(request, jurisdiction!, market, params?.product, request.flow).pipe(
					map((response: RegistrationResponse) =>
						this.action.getRegistrationTokenSuccess(response, request.credential)
					),
					catchError((response: AppError) => of(this.action.getRegistrationTokenFail(response)))
				)
			)
		)
	);

	getRegistrationTokenSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				REGISTRATION_ACTION_TYPE.getRegistrationTokenSuccess,
				REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenCompleteFlowSuccess,
				REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenSimpleFlowSuccess,
				DGA_REGISTRATION_ACTION_TYPE.getRegistrationTokenSuccess,
				AAMS_REGISTRATION_ACTION_TYPE.getRegistrationTokenSuccess,
				REGISTRATION_WITH_SPID_ID_ACTION_TYPE.getRegistrationTokenSuccess
			),
			map((x: ActionWithPayload<RegistrationResponse>) => x.payload),
			map((response) => this.action.getRegistrationStatus(response))
		)
	);

	getRegistrationStatus$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(REGISTRATION_ACTION_TYPE.getRegistrationStatus),
				map((x: ActionWithPayload<RegistrationResponse>) => x.payload),
				tap(() => {
					this.documentRef.obgEventBus?.dispatch("dynamic-form:send", {
						formId: "account-registration",
						type: "FORCE_SUBMITTING_FLAG",
						data: true,
					});
				}),
				switchMap((registrationResponse) =>
					interval(this.config.getRegistrationStatusInterval()).pipe(
						mergeMap(() => this.service.getStatus(registrationResponse.token)),
						filter(
							(response: RegistrationStatusResponse) =>
								response.status !== REGISTER_MESSAGE_CODE.inProgress
						),
						take(1),
						timeout(this.config.getRegistrationStatusTimeout()),
						map((response: RegistrationStatusResponse) =>
							this.action.getRegistrationStatusSuccess(response, registrationResponse.credential)
						),
						catchError((error: AppError) => of(this.action.getRegistrationStatusFail(error)))
					)
				)
			)
		)
	);

	registrationFinished$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_ACTION_TYPE.getRegistrationStatusSuccess),
			map((x: ActionWithPayload<RegistrationStatusResponse>) => x.payload),
			map((registrationStatusResponse) => {
				if (
					registrationStatusResponse.status === REGISTER_MESSAGE_CODE.completed ||
					registrationStatusResponse.status === REGISTER_MESSAGE_CODE.awaitingAccountVerification
				) {
					return this.action.registrationSuccess(registrationStatusResponse);
				}
				if (
					registrationStatusResponse.reasons[0] === REGISTER_MESSAGE_CODE.blockedByGamstop ||
					registrationStatusResponse.reasons[0] === REGISTER_MESSAGE_CODE.blockedBySga
				) {
					return this.action.registrationFailWithSelfExclusion(registrationStatusResponse);
				}

				// Implementation added ad-hoc to notify the dynamic forms about the errors
				// Replace with `DynamicFormAppCommand.Send` once the types are fixed in app-dynamic-form
				this.documentRef.obgEventBus?.dispatch("dynamic-form:send", {
					formId: "account-registration",
					type: "VALIDATION_ERRORS",
					data: {
						code: registrationStatusResponse.status,
						errors: registrationStatusResponse.reasons.map((error) => ({ field: "", code: error })),
					},
				});

				return this.action.registrationFail(registrationStatusResponse);
			}),
			tap(() => {
				this.documentRef.obgEventBus?.dispatch("dynamic-form:send", {
					formId: "account-registration",
					type: "FORCE_SUBMITTING_FLAG",
					data: false,
				});
			})
		)
	);

	registrationSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_ACTION_TYPE.registrationSuccess),
			map((x: ActionWithPayload<RegistrationStatusResponse>) => x.payload),
			filter((response: RegistrationStatusResponse) => response.status === REGISTER_MESSAGE_CODE.completed),
			withLatestFrom(
				this.store.select(this.selector.getRegistrationDetails()),
				this.store.select(this.registrationWithPersonalNumberSelector.getState()),
				this.store.select(this.authTransactionSelector.getLastStatus()),
				this.store.select(this.authTransactionSelector.getAuthenticationToken())
			),
			map(([payload, registrationDetails, personalNumberState, authStatus, authToken]) => {
				const iovationBlackBox = registrationDetails?.iovationBlackBox;
				const fraudToken = registrationDetails?.fraudToken;

				if (registrationDetails?.additionalDetails) {
					this.store.dispatch(
						this.customerManagementAction.updatePersistentData(
							COUNTRY_OF_REGISTRATION,
							registrationDetails.additionalDetails.country
						)
					);

					if (
						this.dataProtectionConfigService
							.getHideGroupConsentCountries()
							.includes(registrationDetails.additionalDetails.country!)
					) {
						this.store.dispatch(
							this.customerManagementAction.updatePersistentData(
								HIDE_GROUP_CONSENT_COUNTRY_OF_REGISTRATION,
								true
							)
						);
					}
				}

				if (
					personalNumberState.personalNumberInformationLevel &&
					personalNumberState.personalNumber &&
					!this.authTransactionConfig.getBankIdSecureStartEnabled() &&
					authStatus !== VerificationStatus.Failed
				) {
					if (this.authTransactionConfig.getLoginEnabled()) {
						return batchActions(
							this.authTransactionAction.initializeAuthTransaction({
								personalNumber: personalNumberState.personalNumber,
							}),
							this.registrationWithPersonalNumberAction.resetState()
						);
					}
					if (payload.credential.password) {
						return this.sessionAction.loginWithPersonalNumber(
							this.service.buildLoginWithPersonalNumberRequest(
								payload.credential,
								personalNumberState.personalNumber,
								iovationBlackBox
							)
						);
					}
				}

				if (this.config.getIsAutomaticLoginDisabledAfterSuccessfulRegistration()) {
					return this.action.registrationFinished();
				}

				if (
					this.authTransactionConfig.getBankIdSecureStartEnabled() &&
					authStatus === VerificationStatus.Completed &&
					authToken
				) {
					return batchActions(
						RouteAction.setQueryParams({ loginFromRegistration: true }),
						this.sessionAction.loginWithToken({
							token: authToken,
							iovationBlackBox: iovationBlackBox,
							fraudToken: fraudToken,
						})
					);
				}

				if (
					this.authTransactionConfig.getBankIdSecureStartEnabled() &&
					authStatus === VerificationStatus.Failed
				) {
					return RouteAction.navigate(CORE_ROUTE.home, undefined, { queryParams: { action: "login" } });
				}

				return this.sessionAction.login(this.service.buildLoginRequest(payload.credential, iovationBlackBox));
			})
		)
	);

	registrationFail$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				REGISTRATION_ACTION_TYPE.getRegistrationTokenFail,
				REGISTRATION_ACTION_TYPE.getRegistrationStatusFail,
				REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenSimpleFlowFail,
				REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenCompleteFlowFail,
				DGA_REGISTRATION_ACTION_TYPE.getRegistrationTokenFail,
				AAMS_REGISTRATION_ACTION_TYPE.getRegistrationTokenFail,
				REGISTRATION_WITH_SPID_ID_ACTION_TYPE.getRegistrationTokenFail
			),
			map((x: ActionWithPayload<AppError>) => x.payload),
			map((error) => this.action.registrationFail(error))
		)
	);

	notifyNativeRegistrationFailed$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					REGISTRATION_ACTION_TYPE.registrationFail,
					REGISTRATION_ACTION_TYPE.registrationFailWithSelfExclusion
				),
				tap(() => this.service.registrationDOMEvent(RegistrationCustomEvent.obgRegistrationFail))
			),
		{ dispatch: false }
	);

	notifyNativeRegistrationSucceeded$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(REGISTRATION_ACTION_TYPE.registrationSuccess),
				tap(() => this.service.registrationDOMEvent(RegistrationCustomEvent.obgRegistrationSuccess))
			),
		{ dispatch: false }
	);

	verifyRegistrationToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_ACTION_TYPE.verifyRegistrationToken),
			map((x: ActionWithPayload<string>) => x.payload),
			switchMap((registrationToken) =>
				this.service.verifyToken(registrationToken).pipe(
					map((registrationVerificationResponse) =>
						this.action.verifyRegistrationTokenSuccess(registrationVerificationResponse)
					),
					catchError((error: AppError) => of(this.action.verifyRegistrationTokenFail(error)))
				)
			)
		)
	);

	clear$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_ACTION_TYPE.registrationSuccess),
			observeOn(asyncScheduler), // run in nextTick as the data is used by other effects on same action
			map(() => this.action.clearRegistrationDetails())
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.map$({
				[REGISTRATION_ACTION_TYPE.registrationSuccess]: (state: RegistrationFeatureState & CommonState) =>
					state.registrationFeature.registration.lastStatus
						? {
								context: REGISTRATION_ACTION_TYPE.getRegistrationToken,
								scope: MessageScopeType.Local,
								type: MessageType.Success,
								messageCode: state.registrationFeature.registration.lastStatus.status,
							}
						: null,
				[REGISTRATION_ACTION_TYPE.registrationFailWithSelfExclusion]: {
					context: REGISTRATION_ACTION_TYPE.getRegistrationStatusSuccess,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: REGISTER_MESSAGE_CODE.blockedByGamstop,
				},
				[REGISTRATION_ACTION_TYPE.registrationFailWithSelfExclusion]: {
					context: REGISTRATION_ACTION_TYPE.getRegistrationStatusSuccess,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: REGISTER_MESSAGE_CODE.blockedBySga,
				},
				[REGISTRATION_ACTION_TYPE.verifyRegistrationTokenSuccess]: {
					context: REGISTRATION_ACTION_TYPE.verifyRegistrationToken,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: REGISTER_MESSAGE_CODE.completed,
				},
			}),
			this.messageService.mapErrorByContext$({
				[REGISTRATION_ACTION_TYPE.getRegistrationStatusFail]: REGISTRATION_ACTION_TYPE.getRegistrationStatus,
				[REGISTRATION_ACTION_TYPE.getRegistrationTokenFail]: REGISTRATION_ACTION_TYPE.getRegistrationToken,
				[REGISTRATION_ACTION_TYPE.verifyRegistrationTokenFail]:
					REGISTRATION_ACTION_TYPE.verifyRegistrationToken,
				[REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenCompleteFlowFail]:
					REGISTRATION_ACTION_TYPE.getRegistrationToken,
				[REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenSimpleFlowFail]:
					REGISTRATION_ACTION_TYPE.getRegistrationToken,
				[DGA_REGISTRATION_ACTION_TYPE.getRegistrationTokenFail]: REGISTRATION_ACTION_TYPE.getRegistrationToken,
				[AAMS_REGISTRATION_ACTION_TYPE.getRegistrationTokenFail]: REGISTRATION_ACTION_TYPE.getRegistrationToken,
				[REGISTRATION_WITH_SPID_ID_ACTION_TYPE.getRegistrationTokenFail]:
					REGISTRATION_ACTION_TYPE.getRegistrationToken,
			}),
			this.messageService.mapClearByContext$({
				[REGISTRATION_ACTION_TYPE.getRegistrationStatus]: REGISTRATION_ACTION_TYPE.getRegistrationStatus,
				[REGISTRATION_ACTION_TYPE.getRegistrationToken]: REGISTRATION_ACTION_TYPE.getRegistrationToken,
				[REGISTRATION_ACTION_TYPE.getCPFRegistrationToken]: REGISTRATION_ACTION_TYPE.getCPFRegistrationToken,
				[REGISTRATION_ACTION_TYPE.verifyRegistrationToken]: REGISTRATION_ACTION_TYPE.verifyRegistrationToken,
				[REGISTRATION_ACTION_TYPE.getRegistrationStatusSuccess]:
					REGISTRATION_ACTION_TYPE.getRegistrationStatusSuccess,
				[REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenCompleteFlow]:
					REGISTRATION_ACTION_TYPE.getRegistrationToken,
				[REGISTRATION_WITH_PERSONAL_NUMBER_ACTION_TYPE.getRegistrationTokenSimpleFlow]:
					REGISTRATION_ACTION_TYPE.getRegistrationToken,
				[REGISTRATION_WITH_SMART_ID_ACTION_TYPE.getSmartIdSimpleRegistrationToken]:
					REGISTRATION_ACTION_TYPE.getRegistrationToken,
				[REGISTRATION_WITH_SMART_ID_ACTION_TYPE.getSmartIdCompleteRegistrationToken]:
					REGISTRATION_ACTION_TYPE.getRegistrationToken,
				[DGA_REGISTRATION_ACTION_TYPE.getRegistrationToken]: REGISTRATION_ACTION_TYPE.getRegistrationToken,
				[AAMS_REGISTRATION_ACTION_TYPE.getRegistrationTokenFail]: REGISTRATION_ACTION_TYPE.getRegistrationToken,
				[REGISTRATION_WITH_SPID_ID_ACTION_TYPE.getRegistrationTokenFail]:
					REGISTRATION_ACTION_TYPE.getRegistrationToken,
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: RegistrationAction,
		private marketSelector: MarketSelector,
		private sessionAction: SessionAction,
		private authTransactionAction: AuthTransactionAction,
		private authTransactionConfig: AuthTransactionConfigService,
		private authTransactionSelector: AuthTransactionSelector,
		private service: RegistrationService,
		private selector: RegistrationSelector,
		private config: RegistrationConfigService,
		private registrationWithPersonalNumberAction: RegistrationWithPersonalNumberAction,
		private registrationWithPersonalNumberSelector: RegistrationWithPersonalNumberSelector,
		private store: Store<RegistrationFeatureState & CommonState>,
		private messageService: MessageService,
		private platformService: PlatformService,
		private dataProtectionConfigService: DataProtectionConfigService,
		private customerManagementAction: CustomerManagementAction,
		private documentRef: DocumentRef
	) {}
}
